import React from 'react';
import { FaTimes, FaExclamationTriangle, FaQuestionCircle, FaInfoCircle } from 'react-icons/fa';
import styles from './ConfirmDialog.module.css';
import { useLockBodyScroll } from '../../hooks';

/**
 * Reusable Confirmation Dialog Component
 * 
 * @param {boolean} isOpen - Whether the dialog is open
 * @param {function} onClose - Callback when dialog is closed
 * @param {function} onConfirm - Callback when user confirms action
 * @param {string} title - Dialog title
 * @param {string} message - Main confirmation message
 * @param {string} confirmText - Text for confirm button (default: "Confirm")
 * @param {string} cancelText - Text for cancel button (default: "Cancel")
 * @param {string} variant - Dialog variant: 'danger', 'warning', 'info', 'question' (default: 'warning')
 * @param {boolean} loading - Whether an action is in progress
 * @param {React.ReactNode} children - Optional additional content to display
 */
const ConfirmDialog = ({
    isOpen,
    onClose,
    onConfirm,
    title = 'Confirm Action',
    message = 'Are you sure you want to proceed?',
    confirmText = 'Confirm',
    cancelText = 'Cancel',
    variant = 'warning',
    loading = false,
    children
}) => {
    // Lock body scroll when dialog is open
    useLockBodyScroll(isOpen);

    if (!isOpen) return null;

    const getIcon = () => {
        switch (variant) {
            case 'danger':
                return <FaExclamationTriangle size={48} color="#e74c3c" />;
            case 'warning':
                return <FaExclamationTriangle size={48} color="#f39c12" />;
            case 'info':
                return <FaInfoCircle size={48} color="#3498db" />;
            case 'question':
                return <FaQuestionCircle size={48} color="#9b59b6" />;
            default:
                return <FaExclamationTriangle size={48} color="#f39c12" />;
        }
    };

    const getConfirmButtonClass = () => {
        switch (variant) {
            case 'danger':
                return styles.btnDanger;
            case 'warning':
                return styles.btnWarning;
            case 'info':
                return styles.btnInfo;
            case 'question':
                return styles.btnPrimary;
            default:
                return styles.btnWarning;
        }
    };

    return (
        <div className={styles.dialogOverlay} onClick={onClose}>
            <div className={styles.dialogContent} onClick={(e) => e.stopPropagation()}>
                <div className={styles.dialogHeader}>
                    <h3>{title}</h3>
                    <button
                        className={styles.closeButton}
                        onClick={onClose}
                        disabled={loading}
                    >
                        <FaTimes />
                    </button>
                </div>
                <div className={styles.dialogBody}>
                    <div className={styles.iconContainer}>
                        {getIcon()}
                    </div>
                    <p className={styles.confirmMessage}>
                        {message}
                    </p>
                    {children && (
                        <div className={styles.additionalContent}>
                            {children}
                        </div>
                    )}
                </div>
                <div className={styles.dialogActions}>
                    <button
                        type="button"
                        className={styles.btnSecondary}
                        onClick={onClose}
                        disabled={loading}
                    >
                        {cancelText}
                    </button>
                    <button
                        type="button"
                        className={getConfirmButtonClass()}
                        onClick={onConfirm}
                        disabled={loading}
                    >
                        {loading ? 'Processing...' : confirmText}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ConfirmDialog;
